import { useEffect } from "react";

const DEFAULTS = {
  siteName: "Astoria Solutions",
  title: "Astoria Solutions — Research, Finance & Auction Advisory",
  description:
    "Astoria Solutions is a knowledge-led firm blending Research, Finance, and Auction advisory into measurable outcomes for enterprises worldwide.",
  image:
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=630&fit=crop&q=80",
  twitter: "@astoriasolutions",
};

const SITE_URL =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_SITE_URL) ||
  (typeof window !== "undefined" ? window.location.origin : "");

const ensureTag = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

const setMeta = (name, content, attr = "name") => {
  if (content == null) return;
  const el = ensureTag(`meta[${attr}="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute(attr, name);
    return m;
  });
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  const el = ensureTag(`link[rel="${rel}"]`, () => {
    const l = document.createElement("link");
    l.setAttribute("rel", rel);
    return l;
  });
  el.setAttribute("href", href);
};

/**
 * useSeo — lightweight per-page SEO/OG/Twitter head manager.
 * Pass a partial object; defaults fill the rest.
 */
export function useSeo({
  title,
  description,
  image,
  type = "website",
  path,
  keywords,
} = {}) {
  useEffect(() => {
    const finalTitle = title
      ? `${title} — ${DEFAULTS.siteName}`
      : DEFAULTS.title;
    const finalDesc = description || DEFAULTS.description;
    const finalImage = image || DEFAULTS.image;
    const url = SITE_URL
      ? `${SITE_URL}${path || (typeof window !== "undefined" ? window.location.pathname : "")}`
      : undefined;

    document.title = finalTitle;

    setMeta("description", finalDesc);
    if (keywords) setMeta("keywords", keywords);

    // Open Graph
    setMeta("og:title", finalTitle, "property");
    setMeta("og:description", finalDesc, "property");
    setMeta("og:image", finalImage, "property");
    setMeta("og:type", type, "property");
    setMeta("og:site_name", DEFAULTS.siteName, "property");
    if (url) setMeta("og:url", url, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", finalTitle);
    setMeta("twitter:description", finalDesc);
    setMeta("twitter:image", finalImage);
    setMeta("twitter:site", DEFAULTS.twitter);

    // Canonical
    if (url) setLink("canonical", url);
  }, [title, description, image, type, path, keywords]);
}

export default useSeo;
